import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useCallback, useState } from 'react';
import { Button, ButtonGroup, Modal, Text } from '@hawkins/components';
import { useTranslation } from 'react-i18next';
import NetflixGradientBackground from 'src/components/elements/NetflixGradientBackground';

const NetflixLogo = () => (
  <div style={{ marginBottom: '40px' }}>
    <img 
      src="/nflx-logo-classic@150w.png" 
      alt="Netflix" 
      style={{ height: '80px' }}
    />
  </div>
);

interface VerificationResult {
  success: boolean;
  tokenValid: boolean;
  requiresMFA?: boolean;
  error?: string;
}

function ResetPasswordDispatcher() {
  const { t } = useTranslation();
  const { resetToken } = useParams<{ resetToken: string }>();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');

  const verifyTokenAndRoute = useCallback(async () => {
    if (!resetToken) {
      navigate('/');
      return;
    }

    try {
      const response = await fetch('/api/verify-reset-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetToken
        })
      });
      
      const data: VerificationResult = await response.json();
      
      if (!data.success || !data.tokenValid) {
        // Invalid token - show error modal
        setErrorMessage(data.error || t('errors.invalidToken'));
        return;
      }

      // Token is valid - route based on MFA requirement
      if (data.requiresMFA) {
        // MFA required - go to MFA flow
        navigate(`/mfa?resetToken=${resetToken}`);
      } else {
        // No MFA required - go directly to password form
        navigate(`/reset/${resetToken}/password-form`);
      }
      
    } catch (error) {
      setErrorMessage(t('errors.tokenVerificationFailed'));
    }
  }, [resetToken, navigate, t]);

  useEffect(() => {
    verifyTokenAndRoute();
  }, [verifyTokenAndRoute]);

  const handleErrorClose = () => {
    setErrorMessage('');
    navigate('/');
  };

  return (
    <NetflixGradientBackground>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '450px'
        }}>
          <NetflixLogo />
          
          <Text style={{ 
            color: 'white', 
            textAlign: 'center', 
            marginBottom: '20px',
            fontSize: '18px'
          }}>
            {errorMessage ? '' : t('common.status.verifying')}
          </Text>
        </div>
      </div>

      {errorMessage && (
        <Modal
          open={true}
          onClose={handleErrorClose}
        >
        <Modal.Header 
          title={t('common.status.error')}
          compact
        />
        <Modal.Body>
          <Text variant="body">
            {errorMessage}
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup justifyContent="flex-end">
            <Button 
              onClick={handleErrorClose} 
              variant="primary"
            >
              {t('common.actions.ok')}
            </Button>
          </ButtonGroup>
        </Modal.Footer>
        </Modal>
      )}
    </NetflixGradientBackground>
  );
}

export default ResetPasswordDispatcher;